/**
 * AdminCabinAcademyHub — Kabin Akademisi Yönetim Merkezi
 * Route: /admin/cabin-academy
 * Roles: admin, superadmin
 *
 * 4 üst metrik kart + 6 modül kartı (3x2 grid).
 * İçerik: client-side aggregate via useCabinAcademyStats.
 */

import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/admin/AdminLayout';
import PageHeader from '../components/admin/PageHeader';
import { useLanguage } from '../contexts/LanguageContext';
import { useCabinAcademyStats } from '../hooks/useCabinAcademyStats';
import { motion } from 'framer-motion';
import {
  Mic,
  HelpCircle,
  Globe,
  Volume2,
  MessageSquare,
  Bot,
  Plus,
  TrendingUp,
  Layers,
  Clock,
  Zap,
  ChevronRight,
  BarChart2,
  Lock,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

// ─── helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

function levelColor(level) {
  if (level === 'beginner') return 'bg-emerald-100 text-emerald-700';
  if (level === 'intermediate') return 'bg-amber-100 text-amber-700';
  if (level === 'advanced') return 'bg-rose-100 text-rose-700';
  return 'bg-gray-100 text-gray-600';
}

// ─── LevelBadges ─────────────────────────────────────────────────────────────

function LevelBadges({ byLevel = {} }) {
  const { t } = useLanguage();
  const LABELS = {
    beginner: t('admin.cabinAcademy.level.beginner'),
    intermediate: t('admin.cabinAcademy.level.intermediate'),
    advanced: t('admin.cabinAcademy.level.advanced'),
  };
  const entries = Object.entries(byLevel).filter(([, v]) => v > 0);
  if (!entries.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {entries.map(([lvl, cnt]) => (
        <span
          key={lvl}
          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${levelColor(lvl)}`}
        >
          {LABELS[lvl] || lvl} {cnt}
        </span>
      ))}
    </div>
  );
}

// ─── CategoryBadges ───────────────────────────────────────────────────────────

const CATEGORY_COLORS = [
  'bg-cyan-100 text-cyan-700',
  'bg-violet-100 text-violet-700',
  'bg-pink-100 text-pink-700',
  'bg-orange-100 text-orange-700',
  'bg-teal-100 text-teal-700',
];

function CategoryBadges({ byCategory = {}, max = 3 }) {
  const entries = Object.entries(byCategory)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, max);
  if (!entries.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {entries.map(([cat, cnt], i) => (
        <span
          key={cat}
          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${CATEGORY_COLORS[i % CATEGORY_COLORS.length]}`}
        >
          {cat.replace(/_/g, ' ')} ({cnt})
        </span>
      ))}
    </div>
  );
}

// ─── MetricCard ───────────────────────────────────────────────────────────────

function MetricCard({ icon: Icon, label, value, color, isLoading }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border-2 border-gray-100 rounded-2xl p-4 shadow-sm flex items-center gap-4"
    >
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon size={20} className="text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-gray-500 truncate">{label}</p>
        {isLoading ? (
          <Skeleton className="h-6 w-16 mt-1" />
        ) : (
          <p className="text-2xl font-display font-bold text-gray-800">{value}</p>
        )}
      </div>
    </motion.div>
  );
}

// ─── ModuleCard ───────────────────────────────────────────────────────────────

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.3 },
  }),
};

function ModuleCard({
  index,
  icon: Icon,
  iconBg,
  title,
  desc,
  total,
  totalLabel,
  extraBadges,
  manageHref,
  manageLabel,
  quickCreateLabel,
  lastUpdated,
  isLoading,
  isPlaceholder = false,
  placeholderNotice,
}) {
  const navigate = useNavigate();

  return (
    <motion.div
      custom={index}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover={isPlaceholder ? {} : { y: -2, boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}
      className={`bg-white border-2 rounded-2xl p-5 flex flex-col gap-3 transition-shadow ${
        isPlaceholder ? 'border-dashed border-gray-200 opacity-70' : 'border-gray-100 shadow-sm'
      }`}
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
          <Icon size={20} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-gray-800 text-base leading-tight">{title}</p>
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{desc}</p>
        </div>
        {isPlaceholder && (
          <Lock size={16} className="text-gray-400 flex-shrink-0 mt-0.5" />
        )}
      </div>

      {/* Stats */}
      <div className="flex-1">
        {isLoading ? (
          <Skeleton className="h-5 w-24" />
        ) : isPlaceholder ? (
          <p className="text-xs text-amber-600 font-semibold bg-amber-50 rounded-lg px-2.5 py-1.5">
            {placeholderNotice}
          </p>
        ) : (
          <>
            <p className="text-sm font-bold text-gray-700">
              <span className="text-2xl font-display text-cyan-600">{total}</span>{' '}
              <span className="text-gray-500">{totalLabel}</span>
            </p>
            {extraBadges}
          </>
        )}
      </div>

      {/* Last updated */}
      {!isPlaceholder && lastUpdated && (
        <div className="flex items-center gap-1 text-[10px] text-gray-400">
          <Clock size={11} />
          <span>{lastUpdated}</span>
        </div>
      )}

      {/* CTA row */}
      {!isPlaceholder && (
        <div className="flex gap-2 pt-1 border-t border-gray-50">
          <Button
            size="sm"
            variant="outline"
            className="flex-1 h-8 text-xs font-semibold border-2 gap-1.5 rounded-xl"
            onClick={() => navigate(`${manageHref}?action=create`)}
          >
            <Plus size={13} />
            {quickCreateLabel}
          </Button>
          <Button
            size="sm"
            className="flex-1 h-8 text-xs font-semibold gap-1.5 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white border-0"
            onClick={() => navigate(manageHref)}
          >
            {manageLabel}
            <ChevronRight size={13} />
          </Button>
        </div>
      )}
    </motion.div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function AdminCabinAcademyHub() {
  const { t, lang } = useLanguage();
  const { isLoading, voiceStats, interviewStats, aviationStats, announceStats, paxStats, aggregate } =
    useCabinAcademyStats();

  // ── metric cards data ──────────────────────────────────────────────────────

  const MODULE_ID_LABELS = {
    voicePractice: lang === 'tr' ? 'Sesli Pratik' : 'Voice Practice',
    voiceInterview: lang === 'tr' ? 'Sesli Mülakat' : 'Voice Interview',
    aviationEnglish: 'Aviation English',
    announceTemplates: lang === 'tr' ? 'Anons Şablonları' : 'Announce Templates',
    paxComm: lang === 'tr' ? 'Yolcu İletişimi' : 'Pax Comm',
  };

  const metrics = [
    {
      icon: Layers,
      label: t('admin.cabinAcademy.metric.activeModules'),
      value: isLoading ? '—' : aggregate.activeModules,
      color: 'bg-cyan-500',
    },
    {
      icon: BarChart2,
      label: t('admin.cabinAcademy.metric.totalAcrossModules'),
      value: isLoading ? '—' : aggregate.totalAcrossModules,
      color: 'bg-emerald-500',
    },
    {
      icon: TrendingUp,
      label: t('admin.cabinAcademy.metric.mostActive'),
      value: isLoading ? '—' : MODULE_ID_LABELS[aggregate.mostActive?.id] || '—',
      color: 'bg-amber-500',
    },
    {
      icon: Clock,
      label: t('admin.cabinAcademy.metric.lastUpdated'),
      value: isLoading ? '—' : formatDate(aggregate.lastUpdatedGlobal),
      color: 'bg-violet-500',
    },
  ];

  // ── module cards data ──────────────────────────────────────────────────────

  const modules = [
    {
      id: 'voicePractice',
      icon: Mic,
      iconBg: 'bg-cyan-500',
      title: t('admin.cabinAcademy.module.voicePractice.title'),
      desc: t('admin.cabinAcademy.module.voicePractice.desc'),
      total: voiceStats.total,
      totalLabel: t('admin.cabinAcademy.module.voicePractice.totalLabel'),
      extraBadges: <LevelBadges byLevel={voiceStats.byLevel} />,
      manageHref: '/admin/voice-practice-scenarios',
      manageLabel: t('admin.cabinAcademy.module.voicePractice.manageBtn'),
      quickCreateLabel: t('admin.cabinAcademy.module.voicePractice.quickCreateBtn'),
      lastUpdated: formatDate(voiceStats.lastUpdated),
      isLoading: voiceStats.isLoading,
    },
    {
      id: 'voiceInterview',
      icon: HelpCircle,
      iconBg: 'bg-violet-500',
      title: t('admin.cabinAcademy.module.voiceInterview.title'),
      desc: t('admin.cabinAcademy.module.voiceInterview.desc'),
      total: interviewStats.total,
      totalLabel: t('admin.cabinAcademy.module.voiceInterview.totalLabel'),
      extraBadges: (
        <div className="flex flex-wrap gap-1 mt-1">
          {Object.entries(interviewStats.byDifficulty || {})
            .filter(([, v]) => v > 0)
            .map(([diff, cnt]) => (
              <span
                key={diff}
                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${levelColor(diff)}`}
              >
                {diff} {cnt}
              </span>
            ))}
        </div>
      ),
      manageHref: '/admin/voice-interview-questions',
      manageLabel: t('admin.cabinAcademy.module.voiceInterview.manageBtn'),
      quickCreateLabel: t('admin.cabinAcademy.module.voiceInterview.quickCreateBtn'),
      lastUpdated: formatDate(interviewStats.lastUpdated),
      isLoading: interviewStats.isLoading,
    },
    {
      id: 'aviationEnglish',
      icon: Globe,
      iconBg: 'bg-sky-500',
      title: t('admin.cabinAcademy.module.aviationEnglish.title'),
      desc: t('admin.cabinAcademy.module.aviationEnglish.desc'),
      total: aviationStats.total,
      totalLabel: t('admin.cabinAcademy.module.aviationEnglish.totalLabel'),
      extraBadges: (
        <>
          <LevelBadges byLevel={aviationStats.byLevel} />
          {aviationStats.totalQuestions > 0 && (
            <p className="text-[10px] text-gray-500 mt-1">
              {t('admin.cabinAcademy.module.aviationEnglish.questionCount').replace(
                '{n}',
                aviationStats.totalQuestions
              )}
            </p>
          )}
        </>
      ),
      manageHref: '/admin/aviation-english-scenarios',
      manageLabel: t('admin.cabinAcademy.module.aviationEnglish.manageBtn'),
      quickCreateLabel: t('admin.cabinAcademy.module.aviationEnglish.quickCreateBtn'),
      lastUpdated: formatDate(aviationStats.lastUpdated),
      isLoading: aviationStats.isLoading,
    },
    {
      id: 'announceTemplates',
      icon: Volume2,
      iconBg: 'bg-amber-500',
      title: t('admin.cabinAcademy.module.announceTemplates.title'),
      desc: t('admin.cabinAcademy.module.announceTemplates.desc'),
      total: announceStats.total,
      totalLabel: t('admin.cabinAcademy.module.announceTemplates.totalLabel'),
      extraBadges: <CategoryBadges byCategory={announceStats.byCategory} />,
      manageHref: '/admin/announce-templates',
      manageLabel: t('admin.cabinAcademy.module.announceTemplates.manageBtn'),
      quickCreateLabel: t('admin.cabinAcademy.module.announceTemplates.quickCreateBtn'),
      lastUpdated: formatDate(announceStats.lastUpdated),
      isLoading: announceStats.isLoading,
    },
    {
      id: 'paxComm',
      icon: MessageSquare,
      iconBg: 'bg-rose-500',
      title: t('admin.cabinAcademy.module.paxComm.title'),
      desc: t('admin.cabinAcademy.module.paxComm.desc'),
      total: paxStats.total,
      totalLabel: t('admin.cabinAcademy.module.paxComm.totalLabel'),
      extraBadges: <CategoryBadges byCategory={paxStats.byCategory} />,
      manageHref: '/admin/pax-comm-situations',
      manageLabel: t('admin.cabinAcademy.module.paxComm.manageBtn'),
      quickCreateLabel: t('admin.cabinAcademy.module.paxComm.quickCreateBtn'),
      lastUpdated: formatDate(paxStats.lastUpdated),
      isLoading: paxStats.isLoading,
    },
    {
      id: 'cabinAiCoach',
      icon: Bot,
      iconBg: 'bg-slate-400',
      title: t('admin.cabinAcademy.module.cabinAiCoach.title'),
      desc: t('admin.cabinAcademy.module.cabinAiCoach.desc'),
      isPlaceholder: true,
      placeholderNotice: t('admin.cabinAcademy.cabinAiCoach.faz2Notice'),
    },
  ];

  return (
    <AdminLayout>
      <PageHeader
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: t('admin.nav.cabinAcademy') },
          { label: t('admin.nav.cabinHub') },
        ]}
        title={t('admin.cabinAcademy.title')}
        subtitle={t('admin.cabinAcademy.subtitle')}
      />

      {/* Metric cards — 4 kart */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {metrics.map((m, i) => (
          <MetricCard
            key={i}
            icon={m.icon}
            label={m.label}
            value={m.value}
            color={m.color}
            isLoading={isLoading && m.value === '—'}
          />
        ))}
      </div>

      {/* Section header */}
      <div className="flex items-center gap-2 mb-4">
        <Zap size={16} className="text-amber-500" />
        <h3 className="font-display font-bold text-gray-700 text-sm uppercase tracking-wide">
          {t('admin.cabinAcademy.modulesSection')}
        </h3>
      </div>

      {/* Module cards — 3x2 grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {modules.map((mod, i) => (
          <ModuleCard
            key={mod.id}
            index={i}
            icon={mod.icon}
            iconBg={mod.iconBg}
            title={mod.title}
            desc={mod.desc}
            total={mod.total}
            totalLabel={mod.totalLabel}
            extraBadges={mod.extraBadges}
            manageHref={mod.manageHref}
            manageLabel={mod.manageLabel}
            quickCreateLabel={mod.quickCreateLabel}
            lastUpdated={mod.lastUpdated}
            isLoading={mod.isLoading}
            isPlaceholder={mod.isPlaceholder}
            placeholderNotice={mod.placeholderNotice}
          />
        ))}
      </div>
    </AdminLayout>
  );
}
